import { CSSProperties } from 'react';
import { Stack, TextField as MuiTextField, TextFieldProps } from '@mui/material';
import { Controller, Control, FieldValues } from 'react-hook-form';

import TextFormLabel from '../text/TextFormLabel';
import TextFormError from '../text/TextFormError';
import { useLocalization } from '../../../localization';

export interface InputProps<T extends FieldValues = FieldValues> extends Omit<TextFieldProps, 'name' | 'control'> {
  name: string;
  control: Control<T>;
  label: string;
  subLabel?: { [key: string]: string | number };
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  style?: CSSProperties;
}

export const InputField = <T extends FieldValues = FieldValues>(props: InputProps<T>) => {
  const {
    name,
    control,
    label,
    subLabel,
    required = false,
    placeholder,
    disabled = false,
    style,
    ...textFieldProps
  } = props;
  const { t } = useLocalization();

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <TextFormLabel label={label} subLabel={subLabel} required={required} />
      </Stack>
      <Controller
        name={name as any}
        control={control}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <>
            <MuiTextField
              value={value || ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder={placeholder ? t(placeholder) : ''}
              disabled={disabled}
              size="small"
              style={{
                resize: 'vertical',
                width: '100%',
                ...style,
              }}
              error={!!error}
              {...textFieldProps}
            />
            <TextFormError error={error} />
          </>
        )}
      />
    </Stack>
  );
};
